import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useLocation } from "react-router-dom";
import { ConnectionStatusDot } from "./ConnectionStatusDot";
import { OfflineBanner } from "./OfflineBanner";
import { ScholarshipChatWidget } from "./ScholarshipChatWidget";
import { User } from "lucide-react";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const location = useLocation();
  
  // Check if user is on admin pages
  const isAdminPage = location.pathname.startsWith('/admin/');
  
  // Check if user is on application pages (for chat widget)
  const isApplicationPage = location.pathname.startsWith('/application');
  
  // Show user info if user is logged in (regular or admin)
  const showUserInfo = !!user;

  // Display name with fallback to email
  const displayName = profile?.displayName || user?.email;
  const hasName = profile?.firstName || profile?.lastName;

  return (
    <div className="min-h-screen">
      {/* Skip Navigation Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:bg-background focus:text-foreground focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      
      <OfflineBanner />
      {/* Header with logo - only for non-admin pages without user info and not home page */}
      {!isAdminPage && !showUserInfo && location.pathname !== '/' && (
        <div className="absolute top-4 left-4 z-50 bg-background/95 backdrop-blur-sm border border-border rounded-lg p-3 shadow-sm">
          <img 
            src="/lovable-uploads/90a392a1-f137-4e62-8e8c-2f1beb9d4f1d.png" 
            alt="Catalino Tapia Scholarship Foundation" 
            className="h-12 w-auto"
            width="192"
            height="32"
            loading="lazy"
          />
        </div>
      )}
      
      {/* User identification bar with logo */}
      {showUserInfo && (
        <div className="w-full bg-background border-b border-border py-3 px-4 z-50 shadow-sm">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <img 
              src="/lovable-uploads/90a392a1-f137-4e62-8e8c-2f1beb9d4f1d.png" 
              alt="Catalino Tapia Scholarship Foundation" 
              className="h-8 w-auto"
              width="128"
              height="21"
              loading="eager"
            />
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground leading-tight">
                    {hasName ? displayName : 'Welcome'}
                  </span>
                  {hasName && (
                    <span className="text-xs text-muted-foreground leading-tight">
                      {user?.email}
                    </span>
                  )}
                  {!hasName && (
                    <span className="text-xs text-muted-foreground leading-tight">
                      {user?.email}
                    </span>
                  )}
                </div>
              </div>
              <ConnectionStatusDot />
            </div>
            <div className="w-8"></div> {/* Spacer for balance */}
          </div>
        </div>
      )}
      
      {/* Page content */}
      <main id="main-content" className={showUserInfo ? "pt-4" : ""}>
        {children}
      </main>
      
      {/* Chat widget available on all pages */}
      <ScholarshipChatWidget />
    </div>
  );
};

export default Layout;
